import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { useState, useEffect } from "react";
import { supabase } from "./supabase";
import Panorama from "./Panorama";
import {
  BrowserRouter,
  Routes,
  Route,
  useParams,
  Link,
  Navigate,
} from "react-router-dom";

import Home from "./Home";
import Terms from "./Terms";
import Privacy from "./Privacy";

function TourViewer() {
  const { id } = useParams();

  const [tour, setTour] = useState(null);
  const [meta, setMeta] = useState(null);
  const [current, setCurrent] = useState(null);
  const [error, setError] = useState(null);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.body.style.background = "#000";

    return () => {
      document.body.style.overflow = "auto";
      document.body.style.background = "#f7f7f7";
    };
  }, []);

  useEffect(() => {
    async function loadTour() {
      try {
        const { data, error } = await supabase
          .from("tours")
          .select("*")
          .eq("id", id);

        if (error) {
          setError("Failed to load tour: " + error.message);
          return;
        }

        if (!data || data.length === 0) {
          setError("Tour not found");
          return;
        }

        const tourData = data[0];

        if (!tourData.data || !tourData.data.nodes) {
          setError("Invalid tour data");
          return;
        }

        setTour(tourData.data);

        setMeta({
          title: tourData.title,
          address: tourData.address,
        });

        const firstNode = Object.keys(tourData.data.nodes)[0];
        setCurrent(firstNode);
      } catch (err) {
        setError("An unexpected error occurred: " + err.message);
      }
    }

    loadTour();
  }, [id]);

  const moveToNode = (target) => {
    if (!target) return;
    setCurrent(target);
  };

  if (error) {
    return <div style={styles.loading}>{error}</div>;
  }

  if (!tour || !current) {
    return <div style={styles.loading}>Loading tour...</div>;
  }

  return (
    <div style={{ width: "100vw", height: "100vh" }}>
      {/* MENU */}
      <div style={styles.menuWrapper(open)}>
        
        {/* HEADER */}
        <div style={styles.menuHeader}>
          <div onClick={() => setOpen(!open)} style={styles.menuButton}>
            ☰
          </div>

          {open && (
            <Link to="/">
              <img src="/logo.png" alt="logo" style={styles.logoInline} />
            </Link>
          )}
        </div>

        {/* CONTENIDO */}
        {open && (
          <div style={styles.dropdown}>
            <div style={styles.title}>
              {meta?.title || "Tour"}
            </div>

            <div style={styles.address}>
              {meta?.address || "Unknown location"}
            </div>

            <div style={styles.room}>
              Room: {tour.nodes[current]?.hotspots?.[0]?.room || current}
            </div>

            <div style={styles.linksSection}>
              <Link to="/terms" style={styles.link}>
                Términos
              </Link>
              <Link to="/privacy" style={styles.link}>
                Privacidad
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* 3D */}
      <Canvas
        camera={{ position: [0, 0, 0.1], fov: 67 }}
        style={{ background: "#000" }}
        gl={{ antialias: true }}
      >
        <Panorama
          node={tour.nodes[current]}
          onNavigate={moveToNode}
        />

        <OrbitControls
          makeDefault
          rotateSpeed={-0.35}
          zoomSpeed={0.6}
          minDistance={0.1}
          maxDistance={1.2}
          minPolarAngle={0.1}
          maxPolarAngle={Math.PI - 0.1}
          enableDamping
        />
      </Canvas>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/tour/:id" element={<TourViewer />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

const styles = {
  loading: {
    width: "100vw",
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#000",
    color: "#fff",
    fontSize: "18px",
  },

  menuWrapper: (open) => ({
    position: "absolute",
    top: "20px",
    left: "20px",
    zIndex: 20,
    borderRadius: "14px",
    overflow: "hidden",
    background: "rgba(0, 100, 50, 0.35)",
    backdropFilter: "blur(12px)",
    width: open ? "230px" : "44px",
    transition: "width 0.25s ease",
    boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
  }),

  menuHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start", // 🔥 antes era space-between
    gap: "6px", // 🔥 controla qué tan pegado está el logo al botón
    padding: "6px",
  },

  menuButton: {
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    cursor: "pointer",
  },

  // 🔥 logo inline (a la derecha)
  logoInline: {
    height: "28px",
    borderRadius: "6px",
    cursor: "pointer",
  },

  dropdown: {
    padding: "10px 14px 14px",
    color: "#fff",
  },

  title: {
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "4px",
  },

  address: {
    fontSize: "12px",
    opacity: 0.85,
    marginBottom: "8px",
  },

  room: {
    fontSize: "13px",
    fontWeight: "500",
    marginBottom: "10px",
  },

  linksSection: {
    borderTop: "1px solid rgba(255,255,255,0.2)",
    paddingTop: "10px",
    display: "flex",
    gap: "12px",
  },

  link: {
    fontSize: "12px",
    color: "#fff",
    textDecoration: "none",
    opacity: 0.85,
  },
};